import React, { useMemo } from 'react'
import classNames from 'classnames'
import styles from './need.module.css'
import { GtmHelper, MatchingFundHelper, TrustedHtml, findApiV4Link, gaEvent } from 'shared'
import { formatCents } from '@betterplace/utils'

import { NeedProps } from './types'
import { useCallback } from 'react'

const useDonateUrl = (project: NeedProps['project'], need: NeedProps['need']): string | undefined => {
  return useMemo(
    () => (!project.donations_prohibited ? findApiV4Link({ ofObject: need, withRel: 'new_donation' }) : undefined),
    [project.donations_prohibited, need]
  )
}

export const Need = ({ project, need }: NeedProps): JSX.Element => {
  const donateUrl = useDonateUrl(project, need)

  const hasMatchingFund = !!MatchingFundHelper.locallyActiveMatchingFund(project)

  const setTracking: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
    (event) => {
      if (!donateUrl) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      project && GtmHelper.pushAddToBasket(project)
      const tracking = (event.target as HTMLAnchorElement).classList.contains('btn')
        ? 'click need_donation_button'
        : 'click need_donation_container'
      gaEvent('donation', tracking, need.title)
    },
    [need.title, project, donateUrl]
  )

  const classes = useMemo(
    () =>
      classNames(styles.container, {
        [styles.disabled]: project && project.donations_prohibited,
      }),
    [project]
  )

  return (
    <div className={classes}>
      <div className={styles.title}>{need.title}</div>
      <div className={styles.amount}>
        {i18n.t('projects.show.needs.still_missing_label', {
          count: need.open_amount_in_cents,
          amount: formatCents(i18n.locale, need.open_amount_in_cents, { precision: 0 }),
          total: formatCents(i18n.locale, need.requested_amount_in_cents, { precision: 0 }),
        })}
      </div>
      <TrustedHtml className={styles.description} as="p" value={need.description} />
      {project && !project.donations_prohibited && (
        <a
          href={donateUrl}
          onClick={setTracking}
          className={classNames('btn btn-primary btn-large', hasMatchingFund)}
          data-testid="ppp-donate-button"
        >
          {i18n.t('projects.show.needs.donate_button_label')}
        </a>
      )}
    </div>
  )
}
